import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store/types';
import { TypeUtils } from '@utils';
import { VehicleStatus } from '@services/fleet-vehicle/vehicle-status.enum';
import { UserInvitationState } from '@services/user-invitation/user-invitation-state.enum';
import { Organization } from '@services/organization';
import { Role } from '@services/role';
import { CreateEasyInputDto, ICreateEasyInputDto } from '@services/easy-input';
import { PackageDealTab } from '@models/package-deal-tab';
import { ProductCategoryType } from '@services/product';

export interface ICommonState {
	sidebarCollapsed?: Maybe<boolean>;
	vehicleStatusTab: VehicleStatus;
	collaboratorsStatusTab: UserInvitationState;
	packageDealTab: PackageDealTab;
	productCategoryTab: ProductCategoryType;
	assumedOrganization?: Maybe<Organization>;
	assumedRole?: Maybe<Role>;
	roleSwitchDialogVisible?: Maybe<boolean>;
	easyInputData: Partial<ICreateEasyInputDto>[];
}

const initialState: ICommonState = {
	sidebarCollapsed: false,
	vehicleStatusTab: VehicleStatus.COMPLETED,
	collaboratorsStatusTab: UserInvitationState.ACCEPTED,
	packageDealTab: PackageDealTab.SERVICES,
	productCategoryTab: ProductCategoryType.CHARGING_POINT,
	easyInputData: [],
};

export const {
	actions: {
		setSidebarCollapsed,
		toggleSidebarCollapsed,
		setVehicleStatusTab,
		setCollaboratorsStatusTab,
		setPackageDealTab,
		setProductCategoryTab,
		setAssumedOrganization,
		setAssumedRole,
		setRoleSwitchDialogVisible,
		updateEasyInputData,
		addEasyInputData,
		removeEasyInputData,
		resetEasyInputData,
	},
	reducer,
} = createSlice({
	name: 'portal',
	initialState,
	reducers: {
		setSidebarCollapsed: (state, data: PayloadAction<Maybe<boolean>>) => {
			state.sidebarCollapsed = data.payload;
		},
		toggleSidebarCollapsed: (state) => {
			state.sidebarCollapsed = !state.sidebarCollapsed;
		},
		setVehicleStatusTab: (state, data: PayloadAction<VehicleStatus>) => {
			state.vehicleStatusTab = data.payload;
		},
		setCollaboratorsStatusTab: (state, data: PayloadAction<UserInvitationState>) => {
			state.collaboratorsStatusTab = data.payload;
		},
		setPackageDealTab: (state, data: PayloadAction<PackageDealTab>) => {
			state.packageDealTab = data.payload;
		},
		setProductCategoryTab: (state, data: PayloadAction<ProductCategoryType>) => {
			state.productCategoryTab = data.payload;
		},
		setAssumedOrganization: (state, data: PayloadAction<Maybe<Organization>>) => {
			state.assumedOrganization = data.payload;
		},
		setAssumedRole: (state, data: PayloadAction<Maybe<Role>>) => {
			state.assumedRole = data.payload;
		},
		setRoleSwitchDialogVisible: (state, data: PayloadAction<Maybe<boolean>>) => {
			state.roleSwitchDialogVisible = data.payload;
		},
		updateEasyInputData: (state, data: PayloadAction<{ index: number; partial: Partial<ICreateEasyInputDto> }>) => {
			const index = data.payload.index;
			!state.easyInputData[index] && (state.easyInputData[index] = { index });
			state.easyInputData[index] = { ...state.easyInputData[index], index, ...data.payload.partial };
		},
		addEasyInputData: (state) => {
			!Array.isArray(state.easyInputData) && (state.easyInputData = []);
			const index = state.easyInputData?.length || 0;
			state.easyInputData.push({ index });
		},
		removeEasyInputData: (state, data: PayloadAction<number>) => {
			state.easyInputData = state.easyInputData.filter((_, i) => i !== data.payload);
			!state.easyInputData.length && (state.easyInputData = [{ index: 0 }]);
		},
		resetEasyInputData: (state) => {
			state.easyInputData = [{ index: 0 }];
		},
	},
});

export const sidebarCollapsedSelector = (state: RootState) => state.Portal.sidebarCollapsed || false;
export const vehicleStatusTabSelector = (state: RootState) => state.Portal.vehicleStatusTab ?? VehicleStatus.COMPLETED;
export const collaboratorsStatusTabSelector = (state: RootState) => state.Portal.collaboratorsStatusTab ?? UserInvitationState.ACCEPTED;
export const packageDealTabSelector = (state: RootState) => state.Portal.packageDealTab ?? PackageDealTab.SERVICES;
export const productCategoryTabSelector = (state: RootState) => state.Portal.productCategoryTab ?? ProductCategoryType.CHARGING_POINT;
export const assumedOrganizationSelector = (state: RootState) => state.Portal.assumedOrganization;
export const assumedRoleSelector = (state: RootState) => state.Portal.assumedRole;
export const roleSwitchDialogVisibleSelector = (state: RootState) => state.Portal.roleSwitchDialogVisible ?? false;
export const easyInputDataSelector = (state: RootState) => TypeUtils.convert(CreateEasyInputDto, state.Portal.easyInputData || []);
